/**
 * Custom-domain detection for the web store.
 *
 * The same React app serves both the platform ("stylon.online/:slug/...")
 * and salon-owned custom domains ("book.salon.com/..."). Domain mode drops
 * the slug from the URL and resolves the salon by host instead.
 *
 * WHY THIS IS NOT A HOSTNAME ALLOW-LIST ANY MORE
 * ----------------------------------------------
 * It used to be: "domain mode = this host is not one of the four hosts I
 * recognise". That is a guess dressed up as a fact, and it guesses wrong for
 * every hostname nobody thought to add — a cloudflare tunnel, an ngrok share
 * link, a staging CNAME, a new CloudFront alias. On such a host the app
 * declared itself single-tenant, `getBasePath()` dropped the salon slug out of
 * every in-app link, and the slug-less route tree then handed
 * `salonSlug: undefined` to the product and shop pages — so every product link
 * dead-ended on "Product not found" while the identical click worked on
 * stylon.online. Nothing was wrong with the host: the app had decided it
 * belonged to a business purely because it had never heard of it.
 *
 * A host is a business's own domain when A SALON HAS ACTUALLY BEEN RESOLVED FOR
 * IT — a positive fact, and one the app is already told. `server.js` looks the
 * host up (`GET /config/salon-by-host/:host`) before it sends the HTML and
 * stamps the answer into `window.__SALON_BOOT__` (see `server-helpers.js`
 * `injectMeta()`). That `host` field is written ONLY on the host-resolved
 * branch — never for a slug page, never for a host the lookup 404'd — so the
 * browser knows, before the bundle even runs, whether this hostname really is a
 * linked business domain.
 *
 * The fallback direction is now the safe one: a host we know nothing about is
 * PLATFORM mode, which KEEPS the slug in the URL. A slug that is present but
 * unnecessary is a cosmetically longer link; a slug silently dropped is a dead
 * page.
 *
 * (Serving the app without that injection — a bare `react-scripts start` behind
 * a hosts-file alias — therefore stays in platform mode. To exercise domain mode
 * there, set `window.__SALON_BOOT__ = { host: location.hostname }` before the
 * bundle loads, which is precisely what the server does in production.)
 */

// Platform + local-dev hosts. No longer the decider — the resolved-salon fact
// below is — but kept as an explicit "never a business domain, whatever else
// says", so a stale or forged boot payload cannot turn the platform
// single-tenant.
export const PLATFORM_HOSTS = new Set([
  'stylon.online',
  'www.stylon.online',
  'localhost',
  '127.0.0.1',
]);

const normalizeHost = (h) => String(h || '').trim().toLowerCase().split(':')[0];

/** Current browser hostname, or '' outside a browser (SSR/tests). */
export function getDomainHost() {
  return (typeof window !== 'undefined' && window.location?.hostname) || '';
}

/**
 * The hostname a salon has actually been resolved for, or '' when none has.
 * Read from the server-injected boot payload, which is the record of that
 * lookup having happened and succeeded.
 */
export function getResolvedSalonHost() {
  const boot = (typeof window !== 'undefined' && window.__SALON_BOOT__) || null;
  return normalizeHost(boot && boot.host);
}

/**
 * True when `hostname` is a salon's own custom domain — i.e. a salon has been
 * resolved FOR THIS HOST. Unknown hosts (tunnels, previews, staging, anything
 * new) are platform mode, so the slug stays in the URL. Defaults to the current
 * browser hostname but accepts an explicit value for testability.
 */
export function isDomainMode(hostname = getDomainHost()) {
  const h = normalizeHost(hostname);
  if (!h) return false;
  if (PLATFORM_HOSTS.has(h)) return false;
  return getResolvedSalonHost() === h;
}

/**
 * Base path for building in-app links: '' in domain mode (no slug prefix
 * needed — the host alone identifies the salon), else `/${slug}`.
 */
export function getBasePath(slug, hostname = getDomainHost()) {
  return isDomainMode(hostname) ? '' : `/${slug}`;
}
